import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Download, Upload, FileSpreadsheet, Package, ShoppingCart, ShoppingBag, CheckCircle, AlertCircle, Tag } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BulkUploadPage = () => {
  const [uploading, setUploading] = useState({ products: false, purchases: false, orders: false, 'price-lists': false });
  const [results, setResults] = useState({ products: null, purchases: null, orders: null, 'price-lists': null });

  const downloadTemplate = async (type) => {
    try {
      const response = await axios.get(`${API_URL}/bulk-upload/template/${type}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type.replace('-', '_')}_template.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Template downloaded successfully`);
    } catch (error) {
      toast.error(`Failed to download template`);
    }
  };

  const uploadFile = async (type, file) => {
    if (!file) return;
    
    setUploading({ ...uploading, [type]: true });
    setResults({ ...results, [type]: null });
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API_URL}/bulk-upload/${type}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setResults({ ...results, [type]: response.data });
      toast.success(response.data.message);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || `Failed to upload file`;
      toast.error(errorMessage);
      setResults({ ...results, [type]: { errors: [errorMessage], created: 0 } });
    } finally {
      setUploading({ ...uploading, [type]: false });
    }
  };

  const uploadCategories = [
    {
      key: 'products',
      title: 'Products',
      description: 'Upload products with name, category, unit, price, stock levels, and tax bracket (A/B/C/D)',
      icon: Package,
      color: 'bg-blue-50 text-blue-600'
    },
    {
      key: 'price-lists',
      title: 'Price Lists',
      description: 'Upload customer-specific prices with customer name, product name, and custom price',
      icon: Tag,
      color: 'bg-yellow-50 text-yellow-600'
    },
    {
      key: 'purchases',
      title: 'Purchases',
      description: 'Upload purchase records with product name, quantity, unit cost, supplier, customer (optional), and notes',
      icon: ShoppingBag,
      color: 'bg-green-50 text-green-600'
    },
    {
      key: 'orders',
      title: 'Orders',
      description: 'Upload orders grouped by customer with product details, quantities, and unit prices',
      icon: ShoppingCart,
      color: 'bg-purple-50 text-purple-600'
    }
  ];

  return (
    <div className="space-y-6" data-testid="bulk-upload-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-foreground">Bulk Upload</h1>
          <p className="text-muted-foreground mt-1">Import data using Excel templates</p>
        </div>
      </div>

      <div className="bg-white p-6 rounded-xl border border-border shadow-sm">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
            <FileSpreadsheet className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-heading font-semibold text-foreground">How It Works</h2>
            <p className="text-sm text-muted-foreground">Simple 3-step process to bulk import data</p>
          </div>
        </div>
        
        <div className="grid md:grid-cols-3 gap-4">
          <div className="p-4 bg-muted/30 rounded-lg">
            <div className="flex items-center space-x-2 mb-2">
              <span className="w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-sm font-bold">1</span>
              <span className="font-medium">Download Template</span>
            </div>
            <p className="text-sm text-muted-foreground">Download the Excel template with required columns and example data</p>
          </div>
          <div className="p-4 bg-muted/30 rounded-lg">
            <div className="flex items-center space-x-2 mb-2">
              <span className="w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-sm font-bold">2</span>
              <span className="font-medium">Fill in Data</span>
            </div>
            <p className="text-sm text-muted-foreground">Add your data following the template format. Required fields marked with *</p>
          </div>
          <div className="p-4 bg-muted/30 rounded-lg">
            <div className="flex items-center space-x-2 mb-2">
              <span className="w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-sm font-bold">3</span>
              <span className="font-medium">Upload File</span>
            </div>
            <p className="text-sm text-muted-foreground">Upload the completed Excel file and review the import results</p>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {uploadCategories.map((category) => {
          const Icon = category.icon;
          const result = results[category.key];
          
          return (
            <div key={category.key} className="bg-white p-6 rounded-xl border border-border shadow-sm" data-testid={`upload-card-${category.key}`}>
              <div className="flex items-center space-x-3 mb-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${category.color}`}>
                  <Icon className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-lg font-heading font-semibold text-foreground">{category.title}</h3>
                </div>
              </div>
              
              <p className="text-sm text-muted-foreground mb-6">{category.description}</p>
              
              <div className="space-y-3">
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => downloadTemplate(category.key)}
                  data-testid={`download-${category.key}-template`}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Template
                </Button>
                
                <div className="relative">
                  <Input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={(e) => uploadFile(category.key, e.target.files[0])}
                    disabled={uploading[category.key]}
                    className="absolute inset-0 opacity-0 cursor-pointer"
                    data-testid={`upload-${category.key}-input`}
                  />
                  <Button 
                    className="w-full justify-start"
                    disabled={uploading[category.key]}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {uploading[category.key] ? 'Uploading...' : 'Upload File'}
                  </Button>
                </div>
              </div>
              
              {result && (
                <div className={`mt-4 p-3 rounded-lg ${result.created > 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                  <div className="flex items-center space-x-2 mb-2">
                    {result.created > 0 ? (
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    ) : (
                      <AlertCircle className="w-4 h-4 text-red-600" />
                    )}
                    <span className={`text-sm font-medium ${result.created > 0 ? 'text-green-700' : 'text-red-700'}`}>
                      {result.created} {category.key} created
                    </span>
                  </div>
                  {result.errors && result.errors.length > 0 && (
                    <div className="text-xs text-red-600 space-y-1">
                      {result.errors.slice(0, 3).map((error, idx) => (
                        <p key={idx}>{error}</p>
                      ))}
                      {result.errors.length > 3 && (
                        <p className="text-red-500">...and {result.errors.length - 3} more errors</p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default BulkUploadPage;
